import type { ProviderRoutingInfo, SlotTargetInput, SlotTargetView } from "@/types";

// #P001 (UC-1): die reine Logik der Slot-Liste — Namensregel, Hydrieren aus der Server-Antwort,
// Bauen des Save-Rumpfs, Auswahl fürs kleine Modell. Bewusst OHNE React (Muster #331): das
// Zusammenspiel von Kurzform, write-only-Schlüssel, An/Aus und `smallModel` ist die eigentliche
// Fehlerquelle und lässt sich hier ohne Formular durchtesten.

/** Bearbeitungsstand EINER Slot-Zeile. Alles Rohtext — geprüft wird beim Speichern. */
export interface SlotRow {
  /** Stabile Zeilen-Id fürs Rendering. Der NAME ist editierbar und taugt dafür nicht. */
  id: string;
  /** Slot-Name (= wählbarer Modellname im Mesh). */
  name: string;
  /** Zielmodell beim Anbieter. */
  model: string;
  /** Kontextfenster in Token als Rohtext — leer = nichts vorgeben. */
  ctxTokens: string;
  /** #P001: An/Aus dieses Slots. Nur aktive Slots sind wählbar. */
  enabled: boolean;
  /** #P001: Name des Slots fürs kleine Modell; "" = der Slot selbst. */
  smallModel: string;
  eigen: boolean;
  provider: string;
  baseUrl: string;
  /** Neueingabe des Schlüssels — leer heisst „nicht angefasst" (write-only, #307). */
  credential: string;
  hasCredential: boolean;
  authHeader: string;
  authScheme: string;
  /** #318: zu verwerfende Request-Parameter als Rohtext (kommagetrennt) — leer = keine. */
  dropParams: string;
  /** #P002: darf dieser Slot firmen-heikle Daten verarbeiten? (Aussage des Admins.) */
  sensitiveDataOk: boolean;
  /** #P002: läuft das Ziel lokal oder in der Cloud? */
  hosting: "local" | "cloud";
}

// #P002: der geschlossene Stand — Zwilling von `DEFAULT_SLOT_META` im Master. Ein Slot ohne
// Angabe gilt als Cloud und nicht für heikle Daten geeignet; nur so kann ein Lesefehler einen
// Eintrag nie tauglicher machen, als er ist.
export const STANDARD_META: { sensitiveDataOk: boolean; hosting: "local" | "cloud" } = {
  sensitiveDataOk: false,
  hosting: "cloud",
};

// #P001: Spiegel der Master-Regel (`ModelMapping.pruefeSlotName`). Sie steht hier NICHT, um den
// Server zu ersetzen (er bleibt der Prüfer), sondern damit ein Tippfehler im Namen nicht erst
// nach dem Speichern als nacktes 400 auftaucht.
const SLOT_NAME_ERLAUBT = /^[a-z0-9.-]{1,64}$/;
const ANTHROPIC_ALIASES = ["default", "sonnet", "haiku", "opus"];

/**
 * Prüft einen Slot-Namen. Rückgabe: deutscher Fehlertext oder `null`, wenn er in Ordnung ist.
 */
export function validateSlotName(name: string): string | null {
  const n = name.trim();
  if (n.length === 0) return "Jede Zeile braucht einen Namen.";
  if (ANTHROPIC_ALIASES.includes(n)) {
    return `„${n}“ ist ein Anthropic-Alias und darf nicht überschrieben werden — sonst stünde derselbe Name für zwei verschiedene Ziele.`;
  }
  if (n.startsWith("claude")) {
    return `„${n}“ darf nicht mit „claude“ beginnen — Claude Code schreibt solche Namen vor dem Request um, sie kämen nie am Gateway an.`;
  }
  if (n.includes("[")) {
    return `„${n}“ darf keine eckige Klammer enthalten — Claude Code hängt selbst Suffixe wie „[1m]“ an.`;
  }
  if (!SLOT_NAME_ERLAUBT.test(n)) {
    return `„${n}“ ist kein gültiger Name — erlaubt sind Kleinbuchstaben, Ziffern, Punkt und Bindestrich (max. 64 Zeichen, kein Unterstrich).`;
  }
  return null;
}

// #318: Zu verwerfende Request-Parameter aus dem Rohtext: kommagetrennt, getrimmt, Leeres raus.
// Bewusst KEINE weitere Prüfung hier — die harte Regel (Zeichenvorrat, 16 × 64) macht der Master
// und meldet sie als 400; eine zweite, abweichende Regel in der Oberfläche wäre eine Fehlerquelle.
export function dropParamsWert(roh: string): string[] {
  return roh.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
}

// Kontextfenster aus dem Rohtext: leer = nichts vorgeben (Claude Code entscheidet selbst — bei
// einem Modell, das es kennt, ist das die richtige Wahl). Eine 0 oder Unsinn zaehlt wie leer.
function ctxWert(roh: string): number | null {
  const n = Number(roh.trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Leere Zeile für „Slot hinzufügen". Neu = AUS (default-closed, wie am Master). */
export function emptyRow(id: string): SlotRow {
  return {
    id, name: "", model: "", ctxTokens: "", enabled: false, smallModel: "",
    eigen: false, provider: "", baseUrl: "", credential: "", hasCredential: false,
    authHeader: "", authScheme: "", dropParams: "",
    // #P002: neu = geschlossener Stand. Wer mehr behaupten will, hakt es selbst an.
    sensitiveDataOk: STANDARD_META.sensitiveDataOk, hosting: STANDARD_META.hosting,
  };
}

function rowFromView(id: string, name: string, v: SlotTargetView): SlotRow {
  return {
    id,
    name,
    model: v.model,
    ctxTokens: v.contextWindowTokens ? String(v.contextWindowTokens) : "",
    // Version-Skew: ein älterer Master ohne die #P001-Felder liefert `undefined` → aus / kein
    // kleines Modell. Das ist der ehrliche Stand, nicht geraten.
    enabled: v.enabled === true,
    smallModel: v.smallModel ?? "",
    // „Eigener Anbieter" ist genau dann an, wenn der Slot IRGENDETWAS Eigenes trägt — sonst
    // verschwände beim nächsten Speichern still, was jemand konfiguriert hat.
    eigen: v.provider !== null || v.baseUrl !== null || v.hasCredential || v.authHeader !== null,
    provider: v.provider ?? "",
    baseUrl: v.baseUrl ?? "",
    credential: "",
    hasCredential: v.hasCredential,
    authHeader: v.authHeader ?? "",
    authScheme: v.authScheme ?? "",
    // #318: Die Liste kommt offen aus der Antwort (sie enthält nur Parameter-NAMEN) und wird beim
    // Speichern unverändert wieder mitgeschickt. Genau daran hing der Drift: der Master baut die
    // LiteLLM-Deployments bei jedem Speichern neu, und was das Formular nicht mitsendet, ist
    // danach am Gateway weg — ein von Hand gesetztes `additional_drop_params` überlebte kein
    // einziges Speichern. `?? []` deckt einen älteren Master ab, der das Feld nicht liefert.
    dropParams: (v.litellmDropParams ?? []).join(", "),
    // #P002: derselbe Roundtrip-Grund wie bei `litellmDropParams` — was das Formular nicht
    // mitschickt, ist nach dem Speichern weg. Ein älterer Master ohne das Feld (oder ein
    // Teil-Objekt) liest sich schlüsselweise als geschlossener Stand.
    sensitiveDataOk: v.meta?.sensitiveDataOk === true,
    hosting: v.meta?.hosting === "local" ? "local" : "cloud",
  };
}

/** Server-Stand → Zeilenliste. Die Ids sind stabil aus der Position abgeleitet (kein Zufall). */
export function rowsFromInfo(info: ProviderRoutingInfo | undefined | null): SlotRow[] {
  return Object.entries(info?.mapping ?? {}).map(([name, v], i) => rowFromView(`slot-${i}`, name, v));
}

/** Auswahl fürs kleine Modell: die ANDEREN aktiven Zeilen (leer = der Slot selbst). */
export function smallModelOptions(rows: SlotRow[], rowId: string): string[] {
  const namen = rows
    .filter((r) => r.id !== rowId && r.enabled && r.name.trim().length > 0)
    .map((r) => r.name.trim());
  return [...new Set(namen)];
}

export type MappingResult =
  | { ok: true; mapping: Record<string, SlotTargetInput> }
  | { ok: false; error: string };

/**
 * Zeilenliste → Save-Rumpf (`mapping`). Prüft die Namensregel, Doppelnamen, fehlende Modelle und
 * die `smallModel`-Referenz VOR dem Absenden — der Master prüft dasselbe, aber ein 400 nennt die
 * Zeile nicht.
 */
export function buildMappingFromRows(rows: SlotRow[]): MappingResult {
  const mapping: Record<string, SlotTargetInput> = {};
  // Eine leere Zeile (weder Name noch Modell) ist ein Versehen, kein Fehler — sie fällt raus.
  const echte = rows.filter((r) => r.name.trim().length > 0 || r.model.trim().length > 0);

  for (const r of echte) {
    const name = r.name.trim();
    const namensfehler = validateSlotName(name);
    if (namensfehler) return { ok: false, error: namensfehler };
    if (mapping[name] !== undefined) {
      return { ok: false, error: `Der Name „${name}“ kommt zweimal vor — jeder Slot braucht einen eigenen.` };
    }
    const model = r.model.trim();
    if (model.length === 0) {
      return { ok: false, error: `Der Slot „${name}“ braucht ein Zielmodell.` };
    }

    const drops = dropParamsWert(r.dropParams);
    const smallModel = r.smallModel.trim();
    // #P002: weicht die Aussage vom geschlossenen Stand ab, trägt die Zeile etwas Eigenes —
    // dann muss die lange Form her (die Kurzform kann sie nicht ausdrücken und läse sich beim
    // nächsten Mal als Default zurück). Dieselbe Regel wie `toJSON` im Master.
    const metaEigen =
      r.sensitiveDataOk !== STANDARD_META.sensitiveDataOk || r.hosting !== STANDARD_META.hosting;
    // #307/#318: Kurzform (blosser Modellname) nur, wenn die Zeile NICHTS trägt, was die
    // Kurzform nicht ausdrücken kann. `enabled` gehört dazu: über die Wire-Regel „fehlt ⇒ aus"
    // wäre ein eingeschalteter Slot als Kurzform still ausgeschaltet.
    if (!r.eigen && !r.enabled && smallModel.length === 0 && r.ctxTokens.trim().length === 0 && drops.length === 0 && !metaEigen) {
      mapping[name] = model;
      continue;
    }
    mapping[name] = {
      model,
      enabled: r.enabled,
      // #P002: in der langen Form IMMER vollständig — die kanonische Form, kein Delta. Der
      // Master liest ein Teil-Objekt zwar default-closed, aber eine Oberfläche, die ihre eigene
      // Anzeige nicht zurückschickt, ist genau der Drift aus #318.
      meta: { sensitiveDataOk: r.sensitiveDataOk, hosting: r.hosting },
      // Leer = der Slot selbst (der Master setzt dann `ANTHROPIC_SMALL_FAST_MODEL` auf den
      // Slot-Namen). `null` ist die ausdrückliche Form dafür.
      smallModel: smallModel.length > 0 ? smallModel : null,
      contextWindowTokens: ctxWert(r.ctxTokens),
      // Leer = Schlüssel weglassen (nicht `[]`): das Feld ist nicht write-only, ein geleertes
      // Formularfeld heißt hier „keine mehr" — und dafür ist ein fehlender Schlüssel die
      // richtige, vom Master so gelesene Form.
      ...(drops.length > 0 ? { litellmDropParams: drops } : {}),
      ...(r.eigen
        ? {
            provider: r.provider.trim() || null,
            baseUrl: r.baseUrl.trim() || null,
            // leer = nicht angefasst (der Server behält den hinterlegten Wert)
            ...(r.credential.length > 0 ? { credential: r.credential } : {}),
            authHeader: r.authHeader.trim() || null,
            authScheme: r.authScheme.trim() || null,
          }
        : {}),
    };
  }

  // #P001: `smallModel` muss ein anderer AKTIVER Slot sein (dieselbe Regel wie im Master —
  // ohne sie käme ein 400 „INVALID_MODEL_MAPPING" ohne Hinweis auf die Zeile). Geprüft werden
  // NUR eingeschaltete Zeilen — genau wie `activeEntries()` im Master: ein ausgeschalteter Slot
  // darf einen alten Verweis behalten (Review MED-2: sonst liessen sich zwei Slots, die
  // aufeinander zeigen, nicht gemeinsam abschalten).
  const aktive = new Set(echte.filter((r) => r.enabled).map((r) => r.name.trim()));
  for (const r of echte.filter((r) => r.enabled)) {
    const smallModel = r.smallModel.trim();
    if (smallModel.length === 0) continue;
    if (!aktive.has(smallModel)) {
      return {
        ok: false,
        error: `Der Slot „${r.name.trim()}“ verweist als kleines Modell auf „${smallModel}“ — das muss ein anderer, eingeschalteter Slot sein.`,
      };
    }
    if (smallModel === r.name.trim()) {
      return {
        ok: false,
        error: `Der Slot „${r.name.trim()}“ kann nicht sein eigenes kleines Modell sein — leer lassen heisst genau das.`,
      };
    }
  }

  return { ok: true, mapping };
}
